import React from "react";
import { Table, Text, Button, Icon } from "@virkesborsen/ui-component-library";
import { Navigation } from "../../context";
import { useMetricData } from "../hooks/UseMetricData";
import Skeleton from "../../comps/Skeleton";
import { formatNumber } from "../../utils";
import { SubscriptionCoverRow } from "../components/SubscriptionCovers";

const ITEMS_PER_PAGE_STORAGE_KEY = "overblick_sections_per_page";
const CURRENT_PAGE_STORAGE_KEY = "overblick_current_page";
const ITEMS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

const tableSkeleton = (length) => {
  return (
    <>
      <div className="overflow-x-auto hidden lg:block">
        <Table className="w-full">
          <Table.Header>
            <Table.Row className="border-b border-gray-200">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                <Table.Head key={i} className="pb-3">
                  <Skeleton className="h-4 w-14 mb-2" />
                </Table.Head>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {[...Array(length)].map((_, row) => (
              <Table.Row key={row} className="border-b border-gray-100">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((col) => (
                  <Table.Cell key={col} className="py-3 px-2">
                    <Skeleton className="h-4 my-2 w-full" />
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
      <div className="block lg:hidden space-y-3">
        {[...Array(length)].map((_, row) => (
          <div key={row} className="rounded-lg-alt p-3 gap-3 flex flex-col">
            <div className="flex justify-between items-center">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-6" />
            </div>

            <div className="flex flex-wrap gap-2 justify-start">
              <div className="flex flex-row gap-1">
                <Skeleton className="h-5 w-6 rounded-md" />
                <Skeleton className="h-5 w-6 rounded-md" />
              </div>
              <div className="flex flex-row gap-1">
                <Skeleton className="h-5 w-8 rounded-md" />
                <Skeleton className="h-5 w-14 rounded-md" />
                <Skeleton className="h-5 w-14 rounded-md" />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Skeleton className="h-3 w-7" />
              <Skeleton className="h-3 w-7" />
              <Skeleton className="h-3 w-7" />
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const PaginationController = ({
  currentPage,
  totalPages,
  itemsPerPage,
  onItemsPerPageChange,
  onFirstPage,
  onPreviousPage,
  onNextPage,
  onLastPage,
}) => (
  <div className="flex items-center justify-end p-2 lg:p-4">
    <div className="flex gap-4 items-end flex-col lg:flex-row lg:gap-8 lg:items-center">
      <div className="flex items-center gap-2">
        <Text variant="text-sm/leading-normal/medium">Rader per sida</Text>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {ITEMS_PER_PAGE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <Text variant="text-sm/leading-normal/medium">
        Sida {currentPage} av {totalPages}
      </Text>
      <div className="flex items-center gap-2">
        <Button
          tw
          variant="outline"
          onClick={onFirstPage}
          disabled={currentPage === 1}
        >
          &lt;&lt;
        </Button>
        <Button
          tw
          onClick={onPreviousPage}
          disabled={currentPage === 1}
          variant="outline"
        >
          &lt;
        </Button>
        <Button
          tw
          onClick={onNextPage}
          disabled={currentPage === totalPages}
          variant="outline"
        >
          &gt;
        </Button>
        <Button
          tw
          onClick={onLastPage}
          disabled={currentPage === totalPages}
          variant="outline"
        >
          &gt;&gt;
        </Button>
      </div>
    </div>
  </div>
);

export default function TableWidget() {
  const NavigationContext = React.useContext(Navigation);
  const { sectionsDetails, loading, propDfr } = useMetricData();

  const [currentPage, setCurrentPage] = React.useState(() => {
    const storedValue = sessionStorage.getItem(CURRENT_PAGE_STORAGE_KEY);

    return storedValue ? Number(storedValue) : 1;
  });
  const [itemsPerPage, setItemsPerPage] = React.useState(() => {
    const storedValue = sessionStorage.getItem(ITEMS_PER_PAGE_STORAGE_KEY);

    return storedValue ? Number(storedValue) : 10;
  });
  const sections = sectionsDetails || [];
  const totalPages = React.useMemo(
    () => Math.max(1, Math.ceil(sections.length / itemsPerPage)),
    [sections.length, itemsPerPage]
  );

  const hasInitializedRef = React.useRef(false);

  React.useEffect(() => {
    if (!hasInitializedRef.current && !loading && sections.length > 0) {
      hasInitializedRef.current = true;
    }
  }, [loading, sections.length]);

  React.useEffect(() => {
    sessionStorage.setItem(ITEMS_PER_PAGE_STORAGE_KEY, String(itemsPerPage));
  }, [itemsPerPage]);

  React.useEffect(() => {
    sessionStorage.setItem(CURRENT_PAGE_STORAGE_KEY, String(currentPage));
  }, [currentPage]);

  React.useEffect(() => {
    if (
      hasInitializedRef.current &&
      currentPage > totalPages &&
      totalPages > 0
    ) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const openSectionInMap = (sectionNumber) => {
    // Because ContentView in MinSkog.js is a scrollable container, we  need to get the scroll position from it.
    const mainContainer = document.getElementById("mainContainer");
    const scrollPosition = mainContainer ? mainContainer.scrollTop : 0;

    sessionStorage.setItem("overblickScrollPosition", String(scrollPosition));

    const compObj = {
      comp: "karta",
      section: sectionNumber,
      active: NavigationContext.view.active,
    };

    history.pushState(compObj, "");
    NavigationContext.setView(compObj);
  };

  const getHklColor = (hkl) => {
    if (!propDfr) {
      return "bg-[#BBBBBB]";
    }
    switch (hkl) {
      case "S1":
        return "bg-[#D5EE64]";
      case "S2":
        return "bg-[#D5EE64]";
      case "G1":
        return "bg-[#3F8888]";
      case "G2":
        return "bg-[#3F8888]";
      case "R":
        return "bg-[#7C6ED9]";
      case "K":
        return "bg-[#D1CBC3]";
      case "IA":
        return "bg-[#D84E46]";
    }
  };

  const getBonitetColor = (bonitet) => {
    if (bonitet < 3) {
      return "bg-[#DC2626]";
    } else if (bonitet >= 3 && bonitet < 5) {
      return "bg-[#FFD600]";
    } else if (bonitet >= 5 && bonitet < 7) {
      return "bg-[#EA580C]";
    } else if (bonitet >= 7) {
      return "bg-[#059669]";
    }
  };

  const getAgeSpan = (age) => {
    if (age <= 10) {
      return "0-10";
    } else if (age <= 20) {
      return "10-20";
    } else if (age <= 30) {
      return "20-30";
    } else if (age <= 40) {
      return "30-40";
    } else if (age <= 50) {
      return "40-50";
    } else if (age <= 60) {
      return "50-60";
    } else if (age <= 70) {
      return "60-70";
    } else if (age <= 80) {
      return "70-80";
    } else if (age <= 90) {
      return "80-90";
    } else if (age < 100) {
      return "90-100";
    } else {
      return "100+";
    }
  };

  const currentSections = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sections.slice(startIndex, endIndex);
  }, [sections, currentPage, itemsPerPage]);

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () =>
    setCurrentPage((prev) => Math.max(1, prev - 1));
  const goToNextPage = () =>
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));

  if (!loading && sections.length === 0) {
    return null;
  }

  if (loading) {
    return tableSkeleton(10);
  }

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden lg:block">
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head>
                <Text variant="text-sm/leading-normal/medium">Avd.</Text>
              </Table.Head>
              <Table.Head>
                <Text variant="text-sm/leading-normal/medium">Fastighet</Text>
              </Table.Head>
              <Table.Head>
                <Text variant="text-sm/leading-normal/medium">HKL</Text>
              </Table.Head>
              {/* <Table.Head>
                <Text variant="text-sm/leading-normal/medium">Bonitet</Text>
              </Table.Head> */}
              <Table.Head>
                <Text variant="text-sm/leading-normal/medium">SI</Text>
              </Table.Head>
              <Table.Head>
                <Text variant="text-sm/leading-normal/medium">Ålder</Text>
              </Table.Head>
              <Table.Head>
                <Text variant="text-sm/leading-normal/medium">Area (ha)</Text>
              </Table.Head>
              <Table.Head>
                <Text variant="text-sm/leading-normal/medium">T | G | L</Text>
              </Table.Head>
              <Table.Head>
                <Text variant="text-sm/leading-normal/medium">
                  Volym (m³sk)
                </Text>
              </Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {currentSections.map((section, i) => (
              <Table.Row key={i} onClick={() => openSectionInMap(section.nr)}>
                <Table.Cell>
                  <Text variant="text-sm/leading-normal/normal">
                    #{section.avd}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex items-center">
                    <Text variant="text-sm/leading-normal/normal">
                      {section.designation}
                    </Text>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <SubscriptionCoverRow
                    disableIcon={true}
                    shouldBlur={!propDfr}
                  >
                    <Text
                      variant="text-xs/leading-normal/medium"
                      className={`rounded-md ${getHklColor(
                        section.hkl
                      )} text-white-50 px-2 py-0-5`}
                    >
                      {section.hkl}
                    </Text>
                  </SubscriptionCoverRow>
                </Table.Cell>

                <Table.Cell>
                  <SubscriptionCoverRow
                    disableIcon={true}
                    className="flex items-center"
                    shouldBlur={!propDfr}
                  >
                    <Text
                      variant="text-xs/leading-normal/medium"
                      className="rounded-md border-1 border-[#E6E6E6] px-2 py-0-5"
                    >
                      {section.si}
                    </Text>
                  </SubscriptionCoverRow>
                </Table.Cell>
                <Table.Cell>
                  <SubscriptionCoverRow
                    disableIcon={true}
                    className="flex items-center"
                    shouldBlur={!propDfr}
                  >
                    <Text
                      variant="text-xs/leading-normal/medium"
                      className="rounded-md border-1 border-[#E6E6E6] px-2 py-0-5"
                    >
                      {getAgeSpan(section.age)}
                    </Text>
                  </SubscriptionCoverRow>
                </Table.Cell>
                <Table.Cell>
                  <SubscriptionCoverRow
                    disableIcon={true}
                    shouldBlur={!propDfr}
                    className="flex items-center"
                  >
                    <Text
                      variant="text-xs/leading-normal/medium"
                      className="rounded-md border-1 border-[#E6E6E6] px-2 py-0-5"
                    >
                      {formatNumber(section.area)}
                    </Text>
                  </SubscriptionCoverRow>
                </Table.Cell>
                <Table.Cell>
                  <SubscriptionCoverRow
                    disableIcon={true}
                    className="flex items-center gap-2"
                    shouldBlur={!propDfr}
                  >
                    <div className="flex items-center gap-1">
                      <div className="h-2 w-2 rounded-full bg-[#C0662A]" />

                      <Text variant="text-xs/leading-normal/normal">
                        T{section.tgl.pine} %
                      </Text>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="h-2 w-2 rounded-full bg-[#15803D]" />

                      <Text variant="text-xs/leading-normal/normal">
                        G{section.tgl.fir} %
                      </Text>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="h-2 w-2 rounded-full bg-[#1A6380]" />

                      <Text variant="text-xs/leading-normal/normal">
                        L{section.tgl.maple} %
                      </Text>
                    </div>
                  </SubscriptionCoverRow>
                </Table.Cell>
                <Table.Cell>
                  <SubscriptionCoverRow shouldBlur={!propDfr}>
                    <Text variant="text-sm/leading-normal/normal">
                      {section.volume}
                    </Text>
                  </SubscriptionCoverRow>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        <PaginationController
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          onFirstPage={goToFirstPage}
          onPreviousPage={goToPreviousPage}
          onNextPage={goToNextPage}
          onLastPage={goToLastPage}
        />
      </div>

      {/* Mobile Tablet */}
      <div className="lg:hidden">
        {currentSections.map((section, i) => (
          <div
            key={i}
            className="rounded-lg-alt p-3 gap-3 flex flex-col hover:bg-cloud-200 cursor-pointer"
            onClick={() => openSectionInMap(section.nr)}
          >
            <div className="flex justify-between items-center">
              <Text variant="text-xs/leading-normal/normal">
                #{section.avd} · {section.designation}
              </Text>
              <div
                className="flex flex-row items-baseline gap-0-5 justify-end"
                style={!propDfr ? { filter: "blur(5px)" } : {}}
              >
                <Text
                  variant="text-sm/leading-normal/medium"
                  className="text-gray-900 font-medium"
                >
                  {section.volume}
                </Text>
                <Text variant="text-xs/leading-none/normal" as="p">
                  m³sk
                </Text>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 justify-start">
              <div className="flex flex-row gap-1">
                <div
                  style={!propDfr ? { filter: "blur(5px)" } : {}}
                  className={`inline-flex items-center px-2 py-0-5 rounded-md ${getHklColor(
                    section.hkl
                  )}`}
                >
                  <Text
                    variant="text-xs/leading-normal/medium"
                    className="text-white-50"
                  >
                    {section.hkl}
                  </Text>
                </div>
                {/* <div
                  className={`inline-flex items-center px-2 py-0-5 rounded-md ${getBonitetColor(
                    section.bonitet
                  )}`}
                >
                  <Text
                    variant="text-xs/leading-normal/semibold"
                    className="text-white-50"
                  >
                    {section.bonitet}
                  </Text>
                </div> */}
              </div>
              <div className="flex flex-row gap-1">
                <div
                  className="inline-flex items-center px-2 py-0-5 rounded-md border-1 border-[#E6E6E6]"
                  style={!propDfr ? { filter: "blur(5px)" } : {}}
                >
                  <Text variant="text-xs/leading-normal/medium">
                    {section.si}
                  </Text>
                </div>
                <div
                  className="inline-flex items-center px-2 py-0-5 rounded-md border-1 border-[#E6E6E6]"
                  style={!propDfr ? { filter: "blur(5px)" } : {}}
                >
                  <Text variant="text-xs/leading-normal/medium">
                    Ålder {getAgeSpan(section.age)}
                  </Text>
                </div>
                <div className="inline-flex items-center px-2 py-0-5 rounded-md border-1 border-[#E6E6E6]">
                  <Text variant="text-xs/leading-normal/medium">
                    Area {section.area}
                  </Text>
                </div>
              </div>
            </div>

            <div
              className="flex items-center gap-4"
              style={!propDfr ? { filter: "blur(5px)" } : {}}
            >
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-[#C0662A]" />
                <Text
                  variant="text-xs/leading-normal/normal"
                  className="text-gray-700"
                >
                  T{section.tgl.pine} %
                </Text>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-[#15803D]" />
                <Text
                  variant="text-xs/leading-normal/normal"
                  className="text-gray-700"
                >
                  G{section.tgl.fir} %
                </Text>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-[#1A6380]" />
                <Text
                  variant="text-xs/leading-normal/normal"
                  className="text-gray-700"
                >
                  L{section.tgl.maple} %
                </Text>
              </div>
            </div>
          </div>
        ))}

        {/* Pagination for mobile */}
        {sections.length > 0 && (
          <div className="mt-4">
            <PaginationController
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              onFirstPage={goToFirstPage}
              onPreviousPage={goToPreviousPage}
              onNextPage={goToNextPage}
              onLastPage={goToLastPage}
            />
          </div>
        )}
      </div>
    </>
  );
}
